/**
 * web/src/games/LockInBar.tsx — the one action your turn allows (card eq92fFXqQUuP).
 *
 * The answerer types and locks; the guesser picks one of three or types their
 * own. "Lock in" is literal: once it lands, the server refuses an edit and this
 * bar disappears in favour of a waiting line. One field, one button, stacked
 * and full-width so it works one-handed at 375px.
 */
import { useEffect, useState, type FormEvent } from 'react';
import type { GuessMeRound } from '../../../shared/types';

interface LockInBarProps {
  round: GuessMeRound;
  busy: boolean;
  disabled?: boolean;
  /** Both resolve once the server has ACCEPTED the lock, or reject when it did
   *  not — the draft is kept until then (sol review 2026-10-19). */
  onAnswer: (text: string) => Promise<unknown>;
  onGuess: (text: string) => Promise<unknown>;
}

export function LockInBar({ round, busy, disabled = false, onAnswer, onGuess }: LockInBarProps) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const answerer = round.myRole === 'answerer';
  const options = answerer ? null : round.options;
  const locked = round.myLocked;

  /* Drafts are per round, and a draft that was never ACCEPTED must survive:
     the field used to clear the moment the request was fired, so a network
     error or a refusal erased what the partner had typed (sol review
     2026-10-19). It clears only after the server took it. */
  useEffect(() => {
    setDraft('');
  }, [round.roundId]);

  const pending = busy || sending;

  const submit = async (text: string, send: (t: string) => Promise<unknown>) => {
    if (!text || pending) return;
    setSending(true);
    try {
      await send(text);
      // Accepted: the round moves on (or the bar unmounts), so the draft is
      // spent. A rejection leaves it exactly where it was.
      setDraft('');
    } catch {
      /* the panel surfaces the error; the typed words stay in the field */
    } finally {
      setSending(false);
    }
  };

  const send = (e: FormEvent) => {
    e.preventDefault();
    void submit(draft.trim(), answerer ? onAnswer : onGuess);
  };

  if (locked) return null;

  return (
    <form className="gm-lock" id="gm-lock" data-role={round.myRole} onSubmit={send}>
      {!answerer && options && (
        <div className="gm-options" role="group" aria-label="Your guess">
          {options.map((option, i) => (
            <button
              key={option}
              type="button"
              className="pear-btn gm-option"
              id={`gm-option-${i}`}
              disabled={pending || disabled}
              onClick={() => void submit(option, onGuess)}
            >
              {option}
            </button>
          ))}
        </div>
      )}
      <div className="gm-lock-row">
        <input
          id="gm-input"
          className="pear-chat-input gm-input"
          placeholder={answerer ? 'Your answer…' : 'Or type what you think they said…'}
          aria-label={answerer ? 'Your answer' : 'Your guess'}
          value={draft}
          maxLength={300}
          disabled={pending}
          onChange={e => setDraft(e.target.value)}
        />
        <button
          type="submit"
          className="pear-btn pear-btn-mustard gm-lock-btn"
          id="gm-lock-btn"
          disabled={pending || disabled || draft.trim().length === 0}
        >
          {answerer ? 'Lock it in' : 'Lock in my guess'}
        </button>
      </div>
    </form>
  );
}
